import { ActionType, Action } from "./CoughAnalysisActions";
import {
    CoughAnalysisSet,
    Query,
    QueryOrderDirection,
    CoughSession,
    CoughAnalysisSetType
} from "../misc/api/apiTypes";
import { getQueryFilters, getQuerySorting } from "../misc/components/queryTable/ReducerUtilities";
import { getCachedCoughAnalysisQuery } from "../misc/sessionStorageHelper";
import * as _ from 'lodash';

export interface CoughAnalysisScreenState {
    historyView: {
        selectedAnalysisSetId: string;
        isVisible: boolean;
    },
    cachedCoughAnalysisQuery: Query<CoughAnalysisSet>;
    coughAnalysisSetQuery: Query<CoughAnalysisSet>;
    coughAnalysisSetTypeQuery: Query<CoughAnalysisSetType>;
    sessionQuery: Query<CoughSession>;
    countryQuery: Query<any>;
    deviceSerialQuery: Query<any>;
    cardSerialQuery: Query<any>;
    waveformTypeQuery: Query<any>;
    analystQuery: Query<any>;
    overreadStatusQuery: Query<any>;
}

const initQuery = {
    Page: {
        Current: 1,
        Length: 10
    },
    Order: {
        Member: "Session.CreatedDateTime",
        Direction: QueryOrderDirection.Descending
    },
    Filters: []
};

let initCachedCoughAnalysisQuery = getCachedCoughAnalysisQuery();
initCachedCoughAnalysisQuery = initCachedCoughAnalysisQuery
    ? initCachedCoughAnalysisQuery
    : initQuery;

export const initialScreenState: CoughAnalysisScreenState = {
    historyView: {
        selectedAnalysisSetId: null,
        isVisible: false,
    },
    cachedCoughAnalysisQuery: initCachedCoughAnalysisQuery,
    coughAnalysisSetQuery: initQuery,
    coughAnalysisSetTypeQuery: {
        Members: ["Id", "Name"],
        Order: {
            Member: "Name",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: []
    },
    sessionQuery: {
        Members: ["Session.Name"],
        Order: {
            Member: "Session.Name",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: [],
        Distinct: true
    },
    countryQuery: {
        Members: ["Site.CountryId"],
        Order: {
            Member: "Site.CountryId",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: [],
        Distinct: true
    },
    deviceSerialQuery: {
        Members: ["Session.DeviceSerialNumber", "Session.DeviceSerialPrefix"],
        Order: {
            Member: "Session.DeviceSerialNumber",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: [],
        Distinct: true
    },
    cardSerialQuery: {
        Members: ["Session.MediaSerialNumber", "Session.MediaSerialPrefix"],
        Order: {
            Member: "Session.MediaSerialNumber",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: [],
        Distinct: true
    },
    waveformTypeQuery: {
        Members: ["Id", "Name"],
        Order: {
            Member: "Name",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: [],
        Distinct: true
    },
    analystQuery: {
        Members: ["Analyst.Username"],
        Order: {
            Member: "Analyst.Username",
            Direction: QueryOrderDirection.Ascending
        },
        Filters: [],
        Distinct: true
    },
    overreadStatusQuery: {}
};

export function screenReducer(
    state: CoughAnalysisScreenState = initialScreenState,
    action: Action
): CoughAnalysisScreenState {
    switch (action.type) {
        case ActionType.ClearAnalysisSets:
            return {
                ...state,
                coughAnalysisSetQuery: {
                    ...state.coughAnalysisSetQuery,
                    Data: null
                }
            };
        case ActionType.LoadAnalysisSets:
            return {
                ...state,
                coughAnalysisSetQuery: action.data
            };
        case ActionType.LoadCoughAnalysisSetTypes:
            return {
                ...state,
                coughAnalysisSetTypeQuery: action.query
            };
        case ActionType.LoadSessions:
            return {
                ...state,
                sessionQuery: action.query
            };
        case ActionType.LoadCountries:
            return {
                ...state,
                countryQuery: action.query
            };
        case ActionType.LoadDeviceSerials:
            return {
                ...state,
                deviceSerialQuery: action.query
            };
        case ActionType.LoadCardSerials:
            return {
                ...state,
                cardSerialQuery: action.query
            };
        case ActionType.LoadWaveformTypes:
            return {
                ...state,
                waveformTypeQuery: action.query
            };
        case ActionType.LoadAnalysts:
            return {
                ...state,
                analystQuery: action.query
            };
        case ActionType.LoadOverreadStatuses:
            return {
                ...state,
                overreadStatusQuery: action.query
            };
        case ActionType.FilterAnalysisSets:
            const initFilters = getQueryFilters(
                state.coughAnalysisSetQuery,
                action.member,
                action.condition,
                action.value
            );
            const initQuery = {
                ...state.coughAnalysisSetQuery,
                Filters: initFilters,
                Page: {
                    ...state.coughAnalysisSetQuery.Page,
                    Current: 1
                }
            };

            const cachedFilters = getQueryFilters(
                state.cachedCoughAnalysisQuery,
                action.member,
                action.condition,
                action.value
            );
            const cachedQuery = { ...state.cachedCoughAnalysisQuery, Filters: cachedFilters };

            return {
                ...state,
                coughAnalysisSetQuery: initQuery,
                cachedCoughAnalysisQuery: cachedQuery
            };
        case ActionType.ClearAllFilters:
            return {
                ...state,
                coughAnalysisSetQuery: {
                    ...state.coughAnalysisSetQuery,
                    Filters: []
                }
            };
        case ActionType.SortAnalysisSets:
            const order = getQuerySorting(state.coughAnalysisSetQuery, action.member);

            return {
                ...state,
                coughAnalysisSetQuery: {
                    ...state.coughAnalysisSetQuery,
                    Order: order
                }
            };
        case ActionType.PageAnalysisSets:
            return {
                ...state,
                coughAnalysisSetQuery: {
                    ...state.coughAnalysisSetQuery,
                    Page: {
                        ...state.coughAnalysisSetQuery.Page,
                        Current: action.page
                    }
                }
            };
        case ActionType.CacheAnalysisSets: {
            return {
                ...state,
                cachedCoughAnalysisQuery: action.query
            };
        };
        case ActionType.ToggleHistoryViewVisibility: {
            return {
                ...state,
                historyView: {
                    ...state.historyView,
                    isVisible: !state.historyView.isVisible,
                    selectedAnalysisSetId: action.analysisSetId,
                },
            };
        };
        default:
            return state;
    }
}
